import React from "react";
import PropTypes from "prop-types";
import {
  Avatar,
  Button,
  Divider,
  makeStyles,
  Popover,
  PopoverSurface,
  PopoverTrigger
} from "@fluentui/react-components";
import { FormattedMessage } from "react-intl";
import { Edit20Filled } from "@fluentui/react-icons";

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--Font-size-500, 16px)",
    width: "300px",
    "@media (min-width: 768px)": {
      width: "350px"
    }
  },
  contentHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--fontSizeBase100, 10px)"
  },
  contentHeaderProfile: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "var(--fontSizeBase100, 10px)"
  },
  contentHeaderAvatar: {
    backgroundColor: "var(--colorNeutralBackground1)",
    "& img": {
      objectPosition: "0 20%"
    }
  },
  contentHeaderName: {
    fontSize: "var(--fontSizeBase400, 16px)",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "var(--lineHeightBase300, 20px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    lineClamp: 2,
    boxOrient: "vertical",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflowWrap: "anywhere"
  },
  contentHeaderButton: {
    whiteSpace: "nowrap",
    minWidth: "90px"
  },
  signOutButtonContainer: {
    marginTop: "var(--spacingVerticalL, 16px)",
    width: "100%",
    paddingLeft: "var(--spacingHorizontalL, 16px)",
    paddingRight: "var(--spacingHorizontalL, 16px)"
  },
  editNameButton: {
    marginLeft: "var(--spacingHorizontalMNudge, 10px)"
  }
});

export function AvatarPreviewPopover({ image, name, onEditAvatar, onEditName, onSignOut, open, setOpen }) {
  const styles = useStyles();

  const [isNavigating, setIsNavigating] = React.useState(false);

  const handleEditAvatar = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    setOpen(false);
    onEditAvatar();

    // Reset navigating state after a delay or when popover re-opens
    // Since this component might unmount or the parent might handle navigation,
    // we just want to prevent immediate double clicks.
    setTimeout(() => {
      if (open) setIsNavigating(false);
    }, 1000);
  };

  const handleEditName = () => {
    onEditName();
  };

  // Reset isNavigating when popover opens/closes
  React.useEffect(() => {
    if (open) setIsNavigating(false);
  }, [open]);

  return (
    <>
      <Popover
        positioning={"below-start"}
        withArrow={true}
        size="medium"
        open={open}
        onOpenChange={(_, data) => setOpen(data.open)}
        className={styles.popover}
      >
        <PopoverTrigger disableButtonEnhancement>
          <Avatar
            className={styles.contentHeaderAvatar}
            size={40}
            image={{
              src: image,
              alt: name
            }}
            color
          />
        </PopoverTrigger>
        <PopoverSurface className={styles.content} tabIndex={-1}>
          <div className={styles.contentHeader}>
            <div className={styles.contentHeaderProfile}>
              <Avatar
                className={styles.contentHeaderAvatar}
                size={48}
                image={{
                  src: image,
                  alt: name
                }}
              />
              <div style={{ display: "flex", alignItems: "center" }}>
                <p className={styles.contentHeaderName}>{name}</p>
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<Edit20Filled />}
                  onClick={handleEditName}
                  className={styles.editNameButton}
                />
              </div>
            </div>
            <Button
              appearance="primary"
              size="small"
              className={styles.contentHeaderButton}
              onClick={handleEditAvatar}
              disabled={isNavigating}
            >
              <FormattedMessage id="object-menu.edit-avatar-button" defaultMessage="Change Avatar" />
            </Button>
          </div>

          <Divider />
          <div className={styles.signOutButtonContainer}>
            <Button appearance="outline" onClick={onSignOut} style={{ width: "100%" }}>
              <FormattedMessage id="profile-menu.sign-out" defaultMessage="Sign Out" />
            </Button>
          </div>
        </PopoverSurface>
      </Popover>
    </>
  );
}

AvatarPreviewPopover.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  onEditAvatar: PropTypes.func,
  onSignOut: PropTypes.func,
  onEditName: PropTypes.func,
  open: PropTypes.bool,
  setOpen: PropTypes.func
};
